import type { QueryClient } from "@tanstack/react-query";

import type { CampaignApiRecord, CampaignsListResponse } from "../../types/gamePass";

/** Root query key every campaign listing (per-status) hangs off of. */
export const CAMPAIGNS_LIST_QUERY_KEY = ["game-pass", "campaigns", "list"];

/**
 * Query key for a single campaign record (the Monthly Details page). Kept here
 * so the detail fetcher (`useGetCampaign`) and the cache writers below can't
 * drift on the key shape. Falls back to `""` for a missing id to match the
 * disabled-query key `useGetCampaign` registers while the route resolves.
 */
export const campaignDetailQueryKey = (id?: string) =>
  ["game-pass", "campaigns", "detail", id ?? ""] as const;

const campaignId = (record: {
  id?: string;
  campaign_id?: string;
}): string | undefined => record.id ?? record.campaign_id;

/**
 * Merge an updated campaign record into every cached listing in place, then
 * invalidate so a background refetch reconciles counts / derived fields.
 * Mirrors the season lifecycle hooks' `setQueriesData` + `invalidateQueries`
 * pattern so the two flows behave identically.
 *
 * Also patches the single-record detail cache, since every campaign edit and
 * lifecycle transition (update / schedule / unschedule / complete / cancel /
 * activate) funnels through here. Without it, transitioning a campaign and then
 * opening (or returning to) its Details page renders the stale cached record —
 * e.g. a just-scheduled draft still showing "Draft" — until the 30s stale
 * window elapses. The detail is only patched when already cached: an uncached
 * detail is left untouched so the page fetches a fresh, complete record rather
 * than being seeded with a (possibly partial) transition echo.
 */
export const mergeCampaignIntoLists = (
  queryClient: QueryClient,
  campaign: CampaignApiRecord,
): void => {
  queryClient.setQueriesData<CampaignsListResponse>(
    { queryKey: CAMPAIGNS_LIST_QUERY_KEY },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        campaigns: current.campaigns.map((record) =>
          campaignId(record) === campaignId(campaign)
            ? { ...record, ...campaign }
            : record,
        ),
      };
    },
  );
  queryClient.invalidateQueries({ queryKey: CAMPAIGNS_LIST_QUERY_KEY });

  const id = campaignId(campaign);
  if (id) {
    queryClient.setQueryData<CampaignApiRecord | null>(
      campaignDetailQueryKey(id),
      (current) => (current ? { ...current, ...campaign } : current),
    );
  }
};

/** Drop a deleted campaign from every cached listing, then invalidate. */
export const removeCampaignFromLists = (
  queryClient: QueryClient,
  id: string,
): void => {
  queryClient.setQueriesData<CampaignsListResponse>(
    { queryKey: CAMPAIGNS_LIST_QUERY_KEY },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        campaigns: current.campaigns.filter((record) => campaignId(record) !== id),
      };
    },
  );
  queryClient.invalidateQueries({ queryKey: CAMPAIGNS_LIST_QUERY_KEY });
};
